type ARLogLevel = 'debug' | 'info' | 'warn' | 'error';

interface ARLogPayload {
    source: 'storysplat-ar';
    level: ARLogLevel;
    message: string;
    data?: unknown;
    timestamp: string;
    url?: string;
    userAgent?: string;
}

const DEV_LOG_ENDPOINT = '/__storysplat_ar_log';
const DEV_LOG_HEADER = 'x-storysplat-ar-log';
const DEV_LOG_DEBUG_FLAG = 'storysplatArDebug';
const SENSITIVE_KEY_PATTERN = /(authorization|cookie|set[-_]?cookie|credential|password|secret|session(?:[-_]?id)?|signature|signed|token|jwt|csrf|xsrf|api[-_]?key|key)$/i;
const URL_KEY_PATTERN = /(url|uri|href|baseUrl)$/i;
const ABSOLUTE_URL_IN_TEXT = /\bhttps?:\/\/[^\s"'<>`]+/gi;
const RELATIVE_URL_WITH_QUERY_IN_TEXT = /(^|[\s"'(])\/[^\s"'<>`)]*[?#][^\s"'<>`)]*/g;
const SENSITIVE_ASSIGNMENT_PATTERN = /((?:authorization|cookie|set[-_]?cookie|credential|password|secret|session(?:[-_]?id)?|signature|signed|token|jwt|csrf|xsrf|api[-_]?key|key)=)[^&\s"'<>`]+/gi;
const BEARER_TOKEN_PATTERN = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
const SENSITIVE_HEADER_PATTERN = /\b(authorization|cookie|set-cookie|x-csrf-token|x-xsrf-token|csrf-token|xsrf-token|jwt|api[-_]?key)\s*:\s*[^;\r\n]+/gi;

function isLocalDevHost(hostname: string): boolean {
    return hostname === 'localhost'
        || hostname === '127.0.0.1'
        || hostname === '0.0.0.0'
        || hostname.endsWith('.local')
        || /^10\./.test(hostname)
        || /^192\.168\./.test(hostname)
        || /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
}

function redactUrl(value: string): string {
    try {
        const url = new URL(value, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return url.origin === 'http://localhost' && value.startsWith('/')
            ? url.pathname
            : `${url.origin}${url.pathname}`;
    } catch (_err) {
        return '[redacted-url]';
    }
}

function scrubText(value: string): string {
    return value
        .replace(ABSOLUTE_URL_IN_TEXT, (match) => redactUrl(match))
        .replace(RELATIVE_URL_WITH_QUERY_IN_TEXT, (match, prefix: string) => `${prefix}${redactUrl(match.slice(prefix.length))}`)
        .replace(SENSITIVE_ASSIGNMENT_PATTERN, '$1[redacted]')
        .replace(BEARER_TOKEN_PATTERN, 'Bearer [redacted]')
        .replace(SENSITIVE_HEADER_PATTERN, '$1: [redacted]');
}

function safeData(data: unknown): unknown {
    if (data === undefined) return undefined;

    const seen = new WeakSet<object>();

    try {
        return JSON.parse(JSON.stringify(data, (_key, value) => {
            const key = String(_key);
            if (SENSITIVE_KEY_PATTERN.test(key)) return '[redacted]';
            if (URL_KEY_PATTERN.test(key) && typeof value === 'string') return redactUrl(value);
            if (typeof value === 'string') return scrubText(value);
            if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`;
            if (typeof value === 'bigint') return value.toString();
            if (value instanceof Error) {
                return { name: value.name, message: scrubText(value.message) };
            }
            if (value && typeof value === 'object') {
                if (seen.has(value)) return '[Circular]';
                seen.add(value);
            }
            return value;
        }));
    } catch (_err) {
        return scrubText(String(data));
    }
}

function isDevLogRelayEnabled(): boolean {
    if (typeof window === 'undefined') return false;

    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get(DEV_LOG_DEBUG_FLAG) === '1') return true;
        return window.localStorage?.getItem(DEV_LOG_DEBUG_FLAG) === 'true';
    } catch (_err) {
        return false;
    }
}

function redactedPageUrl(): string | undefined {
    if (typeof window === 'undefined') return undefined;

    try {
        const url = new URL(window.location.href);
        return `${url.origin}${url.pathname}`;
    } catch (_err) {
        return undefined;
    }
}

function shouldRelayToDevServer(): boolean {
    if (typeof window === 'undefined') return false;
    return isLocalDevHost(window.location.hostname) && isDevLogRelayEnabled();
}

function relayToDevServer(payload: ARLogPayload): void {
    if (!shouldRelayToDevServer()) return;

    try {
        const body = JSON.stringify(payload);
        void fetch(DEV_LOG_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', [DEV_LOG_HEADER]: '1' },
            body,
            keepalive: body.length < 60000,
        }).catch(() => {
            // The relay only exists in Vite dev. Ignore missing endpoint/network failures.
        });
    } catch (_err) {
        // Logging must never affect AR runtime behavior.
    }
}

export function arLog(level: ARLogLevel, message: string, data?: unknown): void {
    const safe = safeData(data);
    const safeMessage = scrubText(message);
    const prefix = '[StorySplat AR]';
    const args = safe === undefined ? [prefix, safeMessage] : [prefix, safeMessage, safe];

    if (level === 'error') {
        console.error(...args);
    } else if (level === 'warn') {
        console.warn(...args);
    } else {
        console.log(...args);
    }

    relayToDevServer({
        source: 'storysplat-ar',
        level,
        message: safeMessage,
        data: safe,
        timestamp: new Date().toISOString(),
        url: redactedPageUrl(),
    });
}

export const arDebug = (message: string, data?: unknown): void => arLog('debug', message, data);
export const arInfo = (message: string, data?: unknown): void => arLog('info', message, data);
export const arWarn = (message: string, data?: unknown): void => arLog('warn', message, data);
export const arError = (message: string, data?: unknown): void => arLog('error', message, data);
